import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Eye, EyeOff, ShieldCheck, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/lib/branding";

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const b = useBranding();
  const [valid, setValid] = useState(null); // null=checking, true, false
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`).then((r) => { setValid(r.data.valid); setEmail(r.data.email || ""); }).catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Your password has been reset");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card rounded-xl shadow-xl border border-border overflow-hidden fade-up">
        <div className="bg-secondary px-6 py-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center p-1">{b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}</div>
          <div className="text-white">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent">{b.university_name}</div>
            <div className="font-serif text-xl">Choose a New Password</div>
          </div>
        </div>
        <div className="p-6">
          {valid === null && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Verifying your link…</div>}

          {valid === false && (
            <div className="text-center py-6" data-testid="reset-invalid">
              <XCircle className="h-10 w-10 text-destructive mx-auto" />
              <p className="mt-3 text-sm text-muted-foreground">This reset link is invalid or has expired. Please request a new one.</p>
              <button data-testid="reset-request-new" onClick={() => navigate("/forgot-password")} className="mt-5 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">Request a new link</button>
            </div>
          )}

          {valid === true && done && (
            <div className="text-center py-6" data-testid="reset-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <p className="mt-3 text-sm text-muted-foreground">Your password has been updated. You can now sign in.</p>
              <button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="mt-5 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">Go to Sign in</button>
            </div>
          )}

          {valid === true && !done && (
            <form onSubmit={submit} data-testid="reset-form">
              {email && <p className="text-sm text-muted-foreground mb-5">Setting a new password for <b>{email}</b>.</p>}
              {err && <div className="mb-4 text-sm bg-destructive/10 text-destructive rounded-md px-3 py-2" data-testid="reset-error">{err}</div>}
              <label className="block text-sm font-medium mb-1">New password</label>
              <div className="relative mb-4">
                <input data-testid="reset-password" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="At least 8 characters" className="w-full px-3 py-2.5 pr-10 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
                <button type="button" data-testid="reset-toggle" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
              <label className="block text-sm font-medium mb-1">Confirm password</label>
              <input data-testid="reset-password2" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required className="w-full mb-5 px-3 py-2.5 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
              <button data-testid="reset-submit" type="submit" disabled={busy} className="w-full py-2.5 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">{busy ? "Updating…" : "Update password"}</button>
              <div className="mt-4 flex items-center gap-1.5 text-[11px] text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Your information is private and secured.</div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
